import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import BoxSeatLogo from '../components/BoxSeatLogo';

export default function SeasonPassSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchCurrentUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;

    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/subscription/checkout-status/${sessionId}`);
        if (res.data.payment_status === 'paid' || res.data.status === 'complete') {
          setStatus('success');
          await fetchCurrentUser();
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('error');
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('error');
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, fetchCurrentUser]);

  return (
    <div className="min-h-screen bg-bg text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mb-8">
          <BoxSeatLogo size="md" onClick={() => navigate('/')} />
        </div>

        {status === 'checking' && (
          <div data-testid="payment-processing">
            <Loader2 size={48} className="text-primary animate-spin mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold uppercase mb-2">Confirming Payment...</h2>
            <p className="text-zinc-400 text-sm">Please wait while we verify your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <CheckCircle size={56} className="text-secondary mx-auto mb-4" />
            <h2 className="font-heading text-3xl font-bold uppercase mb-2">You're In!</h2>
            <p className="text-zinc-400 mb-8">Your Season Pass is now active. Welcome to Box Seat.</p>
            <button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="px-8 py-4 bg-primary text-black font-bold uppercase tracking-wide rounded-lg hover:bg-primary-hover transition-all glow-amber"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="payment-error">
            <XCircle size={56} className="text-accent mx-auto mb-4" />
            <h2 className="font-heading text-3xl font-bold uppercase mb-2">Something went wrong</h2>
            <p className="text-zinc-400 mb-8">We couldn't verify your payment. Please try again.</p>
            <button
              onClick={() => navigate('/season-pass')}
              className="px-8 py-4 bg-white/10 text-white font-bold uppercase tracking-wide rounded-lg hover:bg-white/15 transition-all"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
